//src/pages/gro/DimensionsPage.tsx
import { useMemo, useState } from "react";
import { Eye, Pencil, Plus, Power, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { PermissionIconButton } from "@/components/shared/PermissionIconButton";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";

import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { PERMISSIONS } from "@/lib/permissions";
import { groReadPermission, groWritePermission, type GroCrudScope } from "@/lib/groCrudScope";
import {
  useDeleteDimension,
  useDimensions,
  useSetDimensionStatus,
} from "@/hooks/useDimensions";
import type { DimensionListItem, DimensionRiskTheme } from "@/types/dimensions";

const RISK_THEME_LABELS: Record<DimensionRiskTheme, string> = {
  psychosocial: "Psicossocial",
  physical: "Físico",
  chemical: "Químico",
  biological: "Biológico",
  ergonomic: "Ergonômico",
  accident: "Acidente",
  other: "Outro",
};

type DimensionsPageProps = {
  scope?: GroCrudScope;
  basePath?: string;
  title?: string;
  description?: string;
};

export default function DimensionsPage({
  scope = "client",
  basePath = "/gro/dimensions",
  title = "Dimensões",
  description = "Cadastro de dimensões, responsável por agrupar perguntas dos questionários e utilizado para sugestão automática de riscos.",
}: DimensionsPageProps) {
  const navigate = useNavigate();
  const readPermission = groReadPermission(scope, PERMISSIONS.DIMENSIONS_READ);
  const writePermission = groWritePermission(scope, PERMISSIONS.DIMENSIONS_WRITE);

  const [search, setSearch] = useState("");
  const [riskTheme, setRiskTheme] = useState<DimensionRiskTheme | "">("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">(
    "all",
  );
  const [selectedItem, setSelectedItem] = useState<DimensionListItem | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const dimensionsQ = useDimensions({
    q: search,
    risk_theme: riskTheme || undefined,
    is_active: activeFilter === "all" ? undefined : activeFilter === "active",
  }, scope);
  const setStatusMut = useSetDimensionStatus(scope);
  const deleteMut = useDeleteDimension(scope);

  const items = useMemo(() => dimensionsQ.data ?? [], [dimensionsQ.data]);
  const busy = setStatusMut.isPending || deleteMut.isPending;

  async function handleToggleActive(item: DimensionListItem) {
    try {
      await setStatusMut.mutateAsync({
        id: item.id,
        is_active: !item.is_active,
      });
      toast.success(
        item.is_active
          ? "Dimensão desativada com sucesso."
          : "Dimensão ativada com sucesso.",
      );
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Erro ao alterar status da dimensão.");
    }
  }

  async function handleDelete() {
    if (!selectedItem?.id) return;
    try {
      await deleteMut.mutateAsync(selectedItem.id);
      toast.success("Dimensão excluída com sucesso.");
      setConfirmDeleteOpen(false);
      setSelectedItem(null);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Erro ao excluir dimensão.");
    }
  }

  return (
    <div>
      <PageHeader
        title={title}
        description={description}
      >
        <PermissionButton
          permission={writePermission}
          onClick={() => navigate(`${basePath}/new`)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Nova dimensão
        </PermissionButton>
      </PageHeader>

      <Card className="mb-4 p-4">
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.4fr_220px_180px]">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <select
            className="h-10 rounded-md border bg-background px-3 text-sm"
            value={riskTheme}
            onChange={(e) => setRiskTheme(e.target.value as DimensionRiskTheme | "")}
          >
            <option value="">Todos os temas</option>
            <option value="psychosocial">Psicossocial</option>
            <option value="physical">Físico</option>
            <option value="chemical">Químico</option>
            <option value="biological">Biológico</option>
            <option value="ergonomic">Ergonômico</option>
            <option value="accident">Acidente</option>
            <option value="other">Outro</option>
          </select>

          <select
            className="h-10 rounded-md border bg-background px-3 text-sm"
            value={activeFilter}
            onChange={(e) =>
              setActiveFilter(e.target.value as "all" | "active" | "inactive")
            }
          >
            <option value="all">Todos os status</option>
            <option value="active">Ativas</option>
            <option value="inactive">Inativas</option>
          </select>
        </div>
      </Card>

      {dimensionsQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando dimensões...
        </Card>
      ) : dimensionsQ.isError ? (
        <Card className="p-6 text-sm text-destructive">
          {dimensionsQ.error instanceof Error ? dimensionsQ.error.message : "Erro ao carregar dimensões."}
        </Card>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhuma dimensão encontrada"
          description="Cadastre dimensões globais para reutilizar nas perguntas e nas regras de risco."
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dimensão</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[180px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{item.name}</span>
                      <span className="line-clamp-2 text-xs text-muted-foreground">
                        {item.description || "Sem descrição"}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <Badge variant="outline">
                      {RISK_THEME_LABELS[item.risk_theme]}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <Badge variant={item.is_active ? "default" : "secondary"}>
                      {item.is_active ? "Ativa" : "Inativa"}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <PermissionIconButton
                        label="Visualizar"
                        permission={readPermission}
                        onClick={() => navigate(`${basePath}/${item.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </PermissionIconButton>

                      <PermissionIconButton
                        label="Editar"
                        permission={writePermission}
                        disabled={busy}
                        onClick={() => navigate(`${basePath}/${item.id}/editar`)}
                      >
                        <Pencil className="h-4 w-4" />
                      </PermissionIconButton>

                      <PermissionIconButton
                        label={item.is_active ? "Desativar" : "Ativar"}
                        permission={writePermission}
                        disabled={busy}
                        destructive={item.is_active}
                        onClick={() => handleToggleActive(item)}
                      >
                        <Power className="h-4 w-4" />
                      </PermissionIconButton>

                      <PermissionIconButton
                        label="Excluir"
                        permission={writePermission}
                        disabled={busy}
                        destructive
                        onClick={() => {
                          setSelectedItem(item);
                          setConfirmDeleteOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </PermissionIconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Excluir dimensão"
        description={`Deseja realmente excluir a dimensão "${selectedItem?.name ?? ""}"?`}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        destructive
        loading={deleteMut.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
